import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Alert, Button, Chip, Link } from '@mui/material';
import { ArrowBack, FileDownload as FileDownloadIcon, TableChart as XlsxIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { downloadCsv, sanitizeForSpreadsheet } from '../../utils/csv';
import { AccountLedger as IAccountLedger } from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { toLocalIsoDate, firstOfMonthLocalIso } from '../../utils/dateUtils';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ddd',
    fontWeight: 600,
};

const tableCellStyle = {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
};

const formatAmount = (value?: number) =>
    value != null ? value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

const today = new Date();
const firstOfMonth = firstOfMonthLocalIso(today);
const todayStr = toLocalIsoDate(today);

const AccountLedger: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [ledger, setLedger] = useState<IAccountLedger | null>(null);
    const [loading, setLoading] = useState(false);
    const [searchParams] = useSearchParams();
    // Drill-down links from the statements pass the report's date range as ?from=&to=.
    const [from, setFrom] = useState(searchParams.get('from') || firstOfMonth);
    const [to, setTo] = useState(searchParams.get('to') || todayStr);
    const [downloading, setDownloading] = useState(false);
    // Polish P2-1: a restricted cashbook the caller is not listed on answers 403; show a clear state, not an empty ledger.
    const [noAccess, setNoAccess] = useState(false);

    const qFrom = searchParams.get('from');
    const qTo = searchParams.get('to');
    const segmentParam = searchParams.get('segmentId');

    const fetchLedger = async (f: string = from, t: string = to) => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await AccountService.getAccountLedger(id, f, t);
            setNoAccess(false);
            setLedger(data);
        } catch (err) {
            if ((err as { response?: { status?: number } })?.response?.status === 403) {
                setLedger(null);
                setNoAccess(true);
                return;
            }
            toast.showApiError(err, 'Failed to load ledger');
        } finally {
            setLoading(false);
        }
    };

    // Follows the account AND the drill-down query (a link to the same route with new dates must reload).
    useEffect(() => {
        const f = qFrom || from;
        const t = qTo || to;
        setFrom(f);
        setTo(t);
        fetchLedger(f, t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, qFrom, qTo]);

    // Exports the ledger on screen (its own dates), never the unapplied inputs.
    const handleCsv = () => {
        if (!ledger) return;
        const amt = (v?: number | null) => (v ?? 0).toFixed(2);
        const rows: (string | number)[][] = [
            [ledger.fromDate, '', 'Opening Balance', '', '', amt(ledger.openingBalance)],
            ...ledger.lines.map(l => [l.date, sanitizeForSpreadsheet(l.entryNumber),
                sanitizeForSpreadsheet(l.lineDescription || l.entryDescription || ''),
                l.debit != null ? amt(l.debit) : '', l.credit != null ? amt(l.credit) : '', amt(l.runningBalance)]),
            [ledger.toDate, '', 'Closing Balance', amt(ledger.totalDebits), amt(ledger.totalCredits), amt(ledger.closingBalance)],
        ];
        downloadCsv(`account-ledger-${ledger.accountCode}-${ledger.toDate}.csv`,
            ['Date', 'Entry #', 'Description', 'Debit', 'Credit', 'Balance'], rows);
    };

    const handleDownload = async (format: 'pdf' | 'xlsx') => {
        if (!id || !ledger) return;
        try {
            setDownloading(true);
            await AccountService.downloadLedger(id, ledger.fromDate, ledger.toDate, format);
        } catch (err) {
            toast.showApiError(err, 'Failed to download the ledger');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div data-help-page="accounting.ledger.page" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <Button data-help="accounting.ledger.back" startIcon={<ArrowBack />} onClick={() => navigate('/accounting/chart-of-accounts')}>
                    Chart of Accounts
                </Button>
                <h2 style={{ margin: 0 }}>
                    Account Ledger
                    {ledger && (
                        <span style={{ marginLeft: '12px', fontSize: '16px', color: '#555', fontWeight: 400 }}>
                            {ledger.accountCode} — {ledger.accountName}
                            <Chip label={ledger.accountType} size="small" sx={{ ml: 1 }} />
                        </span>
                    )}
                </h2>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <label data-help="accounting.ledger.from" style={{ fontWeight: 500 }}>
                    From:&nbsp;
                    <input
                        type="date"
                        value={from}
                        onChange={e => setFrom(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <label data-help="accounting.ledger.to" style={{ fontWeight: 500 }}>
                    To:&nbsp;
                    <input
                        type="date"
                        value={to}
                        onChange={e => setTo(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <Button data-help="accounting.ledger.apply" variant="contained" onClick={() => fetchLedger()} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
                <Button data-help="accounting.ledger.exportCsv" variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleCsv} disabled={loading || !ledger}>
                    Export CSV
                </Button>
                <Button data-help="accounting.ledger.exportXlsx" variant="outlined" startIcon={<XlsxIcon />} onClick={() => handleDownload('xlsx')} disabled={loading || downloading || !ledger}>
                    Excel
                </Button>
                <Button data-help="accounting.ledger.exportPdf" variant="outlined" startIcon={<PdfIcon />} onClick={() => handleDownload('pdf')} disabled={loading || downloading || !ledger}>
                    PDF
                </Button>
            </div>

            {segmentParam && (
                <div data-testid="ledger-segment-hint" style={{ color: '#666', fontSize: '0.85rem', marginBottom: '12px' }}>
                    Ledger shows all segments — it cannot be filtered by the segment chosen on the report, so its totals may differ from the segment figure.
                </div>
            )}
            {ledger && (
                <div style={{ marginBottom: '12px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                    <span><strong>Total Debits:</strong> {formatAmount(ledger.totalDebits)}</span>
                    <span><strong>Total Credits:</strong> {formatAmount(ledger.totalCredits)}</span>
                    <span><strong>Closing Balance:</strong> {formatAmount(ledger.closingBalance)}</span>
                </div>
            )}

            {noAccess && (
                <Alert severity="warning" data-testid="ledger-no-access" sx={{ mb: 2 }}>
                    You don't have access to this cashbook. Ask an administrator to add you on Banking → Cashbook access.
                </Alert>
            )}
            {!noAccess && (
            <ScrollableTable>
            <table data-help-section="accounting.ledger.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead>
                    <tr>
                        <th data-help="accounting.ledger.col.date" style={tableHeaderStyle}>Date</th>
                        <th data-help="accounting.ledger.col.entry" style={tableHeaderStyle}>Entry #</th>
                        <th data-help="accounting.ledger.col.description" style={tableHeaderStyle}>Description</th>
                        <th data-help="accounting.ledger.col.debit" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Debit</th>
                        <th data-help="accounting.ledger.col.credit" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Credit</th>
                        <th data-help="accounting.ledger.col.balance" style={{ ...tableHeaderStyle, textAlign: 'right' as const }}>Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={6} style={{ ...tableCellStyle, textAlign: 'center' }}>Loading...</td></tr>
                    )}
                    {!loading && (!ledger || ledger.lines.length === 0) && (
                        <tr>
                            <td colSpan={6} style={{ ...tableCellStyle, textAlign: 'center', color: '#888' }}>
                                No posted entries for this account in the selected period.
                            </td>
                        </tr>
                    )}
                    {!loading && ledger && ledger.lines.length > 0 && (
                        <>
                            <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 600 }}>
                                <td style={tableCellStyle}>{ledger.fromDate}</td>
                                <td style={tableCellStyle}>—</td>
                                <td style={tableCellStyle}>Opening Balance</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}></td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}></td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(ledger.openingBalance)}</td>
                            </tr>
                            {ledger.lines.map((line, i) => (
                                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={tableCellStyle}>{line.date}</td>
                                    <td style={tableCellStyle}>
                                        {line.journalEntryId ? (
                                            <Link data-help="accounting.ledger.action.entry" component={RouterLink} to={`/accounting/journal-entries/${line.journalEntryId}`} underline="hover">
                                                {line.entryNumber}
                                            </Link>
                                        ) : line.entryNumber}
                                        {line.yearEndClose && <Chip size="small" label="Year-end close" color="info" sx={{ ml: 1 }} />}
                                    </td>
                                    <td style={tableCellStyle}>{line.lineDescription || line.entryDescription || '—'}</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.debit)}</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(line.credit)}</td>
                                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 500 }}>{formatAmount(line.runningBalance)}</td>
                                </tr>
                            ))}
                            <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 700 }}>
                                <td style={tableCellStyle}>{ledger.toDate}</td>
                                <td style={tableCellStyle}></td>
                                <td style={tableCellStyle}>Closing Balance</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(ledger.totalDebits)}</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(ledger.totalCredits)}</td>
                                <td style={{ ...tableCellStyle, textAlign: 'right' }}>{formatAmount(ledger.closingBalance)}</td>
                            </tr>
                        </>
                    )}
                </tbody>
            </table>
            </ScrollableTable>
            )}
        </div>
    );
};

export default AccountLedger;
